import { useState, useEffect } from 'react';
import { api, apiPost } from '../api';
import { useI18n } from '../i18n/context';
import './ListPages.css';

export default function ChangePassword() {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Calcular fuerza de contraseña en tiempo real
  useEffect(() => {
    if (newPassword) {
      checkPasswordStrength(newPassword);
    }
  }, [newPassword]);

  const checkPasswordStrength = async (password) => {
    try {
      const response = await api('/superadmin/auth/password-strength', { password });
      setPasswordStrength(response.strength || 0);
      setPasswordValidation(response);
    } catch (err) {
      console.error('Error checking password strength:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const data = await apiPost('/superadmin/auth/change-password', {
        currentPassword,
        newPassword,
        confirmPassword
      });

      if (data.success) {
        setSuccess(t('password.success'));
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordStrength(0);
        setPasswordValidation(null);
        
        // Forzar logout después de cambiar contraseña
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        setError(data.error || t('password.error'));
      }
    } catch (err) {
      setError(err.message || t('password.error'));
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 30) return '#ef4444';
    if (strength < 60) return '#f59e0b';
    return '#22c55e';
  };

  const getPasswordStrengthLabel = (strength) => {
    if (strength < 30) return t('password.weak');
    if (strength < 60) return t('password.medium');
    return t('password.strong');
  };

  const getStrengthBarWidth = (strength) => {
    return `${Math.min(strength, 100)}%`;
  };

  return (
    <div className="sa-list-page sa-password-page">
      <div className="sa-page-header">
        <div className="sa-page-header-main">
          <h1 className="sa-page-title">{t('password.changeTitle')}</h1>
          <p className="sa-page-subtitle">{t('password.changeDescription')}</p>
        </div>
      </div>

      <div className="sa-password-content">
        {error && (
          <div className="sa-toast sa-toast--error" style={{ position: 'relative' }}>
            {error}
          </div>
        )}

        {success && (
          <div className="sa-toast sa-toast--success" style={{ position: 'relative' }}>
            {success}
          </div>
        )}

        <div className="sa-card sa-password-card">
          <div className="sa-card-header">
            <h2 className="sa-card-title">{t('password.changeTitle')}</h2>
          </div>
          <form onSubmit={handleSubmit} className="sa-password-form">
            <div className="sa-form-group">
              <label className="sa-label" htmlFor="currentPassword">{t('password.current')}</label>
              <div className="sa-password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="currentPassword"
                  className="sa-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder={t('password.currentPlaceholder')}
                  required
                />
                <button
                  type="button"
                  className="sa-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  title={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                >
                  {showPassword ? t('login.hide') : t('login.show')}
                </button>
              </div>
            </div>

            <div className="sa-form-group">
              <label className="sa-label" htmlFor="newPassword">{t('password.new')}</label>
              <div className="sa-password-input">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  className="sa-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('password.newPlaceholder')}
                  required
                />
                <button
                  type="button"
                  className="sa-password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? t('login.hidePassword') : t('login.showPassword')}
                  title={showNewPassword ? t('login.hidePassword') : t('login.showPassword')}
                >
                  {showNewPassword ? t('login.hide') : t('login.show')}
                </button>
              </div>
              
              {newPassword && (
                <div className="sa-password-strength">
                  <div className="sa-strength-bar">
                    <div 
                      className="sa-strength-fill" 
                      style={{ 
                        width: getStrengthBarWidth(passwordStrength),
                        backgroundColor: getPasswordStrengthColor(passwordStrength)
                      }}
                    />
                  </div>
                  <span className="sa-strength-label" style={{ color: getPasswordStrengthColor(passwordStrength) }}>
                    {getPasswordStrengthLabel(passwordStrength)}
                  </span>
                </div>
              )}
            </div>

            <div className="sa-form-group">
              <label className="sa-label" htmlFor="confirmPassword">{t('password.confirm')}</label>
              <div className="sa-password-input">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  className="sa-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('password.confirmPlaceholder')}
                  required
                />
                <button
                  type="button"
                  className="sa-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? t('login.hidePassword') : t('login.showPassword')}
                  title={showConfirmPassword ? t('login.hidePassword') : t('login.showPassword')}
                >
                  {showConfirmPassword ? t('login.hide') : t('login.show')}
                </button>
              </div>
            </div>

            {passwordValidation && (
              <div className="sa-password-requirements">
                <h4>{t('password.requirements')}</h4>
                <ul>
                  <li className={passwordValidation.length >= 12 ? 'valid' : 'invalid'}>
                    {t('password.minLength', { min: 12 })}
                  </li>
                  <li className={passwordValidation.complexityScore >= 3 ? 'valid' : 'invalid'}>
                    {t('password.complexity')}
                  </li>
                  <li className="valid">
                    {t('password.noCommon')}
                  </li>
                  <li className="valid">
                    {t('password.noSequences')}
                  </li>
                </ul>
              </div>
            )}

            <button 
              type="submit" 
              className="sa-btn-primary" 
              disabled={loading || (passwordValidation && !passwordValidation.isValid)}
            >
              {loading ? t('password.changing') : t('password.changeButton')}
            </button>
          </form>
        </div>

        <div className="sa-card sa-password-tips">
          <div className="sa-card-header">
            <h2 className="sa-card-title">{t('password.tipsTitle')}</h2>
          </div>
          <ul className="sa-password-tips-list">
            <li>{t('password.tipStrong')}</li>
            <li>{t('password.tipReuse')}</li>
            <li>{t('password.tipManager')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
